<script>
// Module-level so frame lists survive card collapse/expand cycles. Entries
// expire so results tiled during the session eventually become visible.
const FRAMES_TTL_MS = 300_000
const FRAMES_MAX = 200
const framesCache = new Map()

const readCache = (key) => {
  const hit = framesCache.get(key)
  if (!hit) return null
  if (Date.now() - hit.at > FRAMES_TTL_MS) {
    framesCache.delete(key)
    return null
  }
  return hit.promise
}

const writeCache = (key, promise) => {
  framesCache.set(key, { at: Date.now(), promise })
  while (framesCache.size > FRAMES_MAX) {
    framesCache.delete(framesCache.keys().next().value)
  }
}
</script>

<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import { useStore } from 'vuex'
import { bbox } from '@turf/turf'
import { fetchFeatureFrames } from '@/services/monitoringApi'
import { buildMonitoringFrameTileUrl } from '@/lib/tileUrl'
import { bringDrawLayersToTop } from '@/composables/useZLayers'

const props = defineProps({
  orgId: { type: [String, Number], required: true },
  projectId: { type: [String, Number], required: true },
  metric: { type: String, required: true },
  resolution: { type: String, default: null },
  // hovered chart date (YYYY-MM-DD, debounced by the parent); null = latest frame
  date: { type: String, default: null },
  codes: { type: Array, required: true },
  geometries: { type: Object, default: () => ({}) },
})

const emit = defineEmits(['domain'])

const store = useStore()
const map = computed(() => store.getters['geo/getMap'])
const mapReady = computed(() => store.getters['geo/getMapReady'])

const frames = ref({}) // code -> frames payload for the current metric/resolution

const cacheKey = (code) => `${props.projectId}:${code}:${props.metric}:${props.resolution || ''}`

const loadFrames = async () => {
  const { metric, resolution } = props
  await Promise.all(
    props.codes.map(async (code) => {
      const key = cacheKey(code)
      let pending = readCache(key)
      if (!pending) {
        pending = fetchFeatureFrames(props.orgId, props.projectId, code, metric, resolution || undefined)
          .then((data) => {
            data?.frames?.sort((a, b) => a.date.localeCompare(b.date))
            return data
          })
          .catch(() => null)
        writeCache(key, pending)
      }
      const data = await pending
      if (metric === props.metric && (resolution || '') === (props.resolution || '')) {
        frames.value = { ...frames.value, [code]: data }
      }
    })
  )
}

const pickFrame = (payload) => {
  const fs = payload?.frames || []
  if (!fs.length) return null
  if (!props.date) return fs[fs.length - 1]
  const target = Date.parse(props.date)
  let best = fs[0]
  for (const f of fs) {
    if (Math.abs(Date.parse(f.date) - target) < Math.abs(Date.parse(best.date) - target)) best = f
  }
  return best
}

// This component stays imperative on purpose: frame scrubbing swaps tile URLs
// via setTiles() on the live source, which MapSource/MapLayer would handle by
// remounting the layer (a visible flash per scrub step).
const tileUrl = buildMonitoringFrameTileUrl

// Between the project features' fill and their outline: the raster covers the
// metric-tinted fill but keeps the outline visible.
const anchorId = (m) => {
  const line = `project-${props.projectId}-line`
  if (m.getLayer(line)) return line
  return m.getLayer('z-0') ? 'z-0' : undefined
}

const mounted = new Map() // layerId -> { srcId, url, maxZoom, before }

const apply = () => {
  const m = map.value
  if (!m || !m.style || !mapReady.value) return

  const wanted = new Map()
  for (const code of props.codes) {
    const payload = frames.value[code]
    const frame = pickFrame(payload)
    if (!frame) continue
    const url = tileUrl(payload, frame.result_id)
    if (!url) continue
    wanted.set(`mon-frame-${code}`, { code, payload, url, maxZoom: payload.max_zoom || null })
  }

  for (const [id, cur] of [...mounted]) {
    const w = wanted.get(id)
    // A style reload drops layers without telling us; maxzoom is fixed at
    // addSource time, so a resolution switch needs a real rebuild.
    const alive = m.getLayer(id) && m.getSource(cur.srcId)
    if (w && alive && w.maxZoom === cur.maxZoom) continue
    if (m.getLayer(id)) m.removeLayer(id)
    if (m.getSource(cur.srcId)) m.removeSource(cur.srcId)
    mounted.delete(id)
  }

  let touched = false
  for (const [id, w] of wanted) {
    const cur = mounted.get(id)
    if (cur) {
      if (cur.url !== w.url) {
        m.getSource(cur.srcId).setTiles([w.url])
        cur.url = w.url
      }
      const before = anchorId(m)
      if (cur.before !== before) {
        m.moveLayer(id, before)
        cur.before = before
        touched = true
      }
      continue
    }
    const srcId = `${id}-src`
    let bounds
    try {
      bounds = props.geometries[w.code] ? bbox(props.geometries[w.code]) : undefined
    } catch (e) {
      bounds = undefined
    }
    m.addSource(srcId, {
      type: 'raster',
      tiles: [w.url],
      tileSize: 256,
      ...(w.maxZoom ? { maxzoom: w.maxZoom } : {}),
      ...(bounds ? { bounds } : {}),
    })
    const before = anchorId(m)
    m.addLayer(
      {
        id,
        type: 'raster',
        source: srcId,
        // No cross-fade: scrubbing frames should cut, not blur through each other.
        paint: { 'raster-resampling': 'nearest', 'raster-fade-duration': 0, 'raster-opacity': 1 },
      },
      before
    )
    mounted.set(id, { srcId, url: w.url, maxZoom: w.maxZoom, before })
    touched = true
  }

  if (touched) bringDrawLayersToTop(m)
}

// addLayer/moveLayer themselves emit styledata, and apply() is idempotent, so
// the self-triggered pass is a no-op — but guard against unbounded recursion.
let applying = false
const applyGuarded = () => {
  if (applying) return
  applying = true
  try {
    apply()
  } finally {
    applying = false
  }
}

const loadKey = computed(
  () => `${props.metric}|${props.resolution || ''}|${props.codes.join(',')}`
)

// Exactly the inputs that change the tile URL set; props.date enters via pickFrame.
const applyKey = computed(
  () =>
    `${mapReady.value ? 1 : 0}|` +
    props.codes.map((c) => `${c}:${pickFrame(frames.value[c])?.result_id || ''}`).join(',')
)

// Numeric legend bounds only make sense when every rendered feature shares them.
const agreedDomain = computed(() => {
  let agreed
  for (const code of props.codes) {
    const d = frames.value[code]?.render_domain
    if (!d) continue
    if (!agreed) agreed = d
    else if (agreed[0] !== d[0] || agreed[1] !== d[1]) return null
  }
  return agreed || null
})

watch(loadKey, loadFrames, { immediate: true })
watch(applyKey, applyGuarded, { immediate: true })
watch(agreedDomain, (d) => emit('domain', d), { immediate: true })

watch(
  map,
  (m, prev) => {
    if (prev) prev.off('styledata', applyGuarded)
    if (m) m.on('styledata', applyGuarded)
  },
  { immediate: true }
)

onBeforeUnmount(() => {
  const m = map.value
  if (m) m.off('styledata', applyGuarded)
  if (!m || !m.style) return
  for (const [id, cur] of mounted) {
    if (m.getLayer(id)) m.removeLayer(id)
    if (m.getSource(cur.srcId)) m.removeSource(cur.srcId)
  }
  mounted.clear()
})
</script>

<template>
  <slot />
</template>
